import { Injectable } from '@angular/core';
import { QuestionnaireCsrdHttpService } from '@services/datasources/questionnaireCsrd-http.service';
import { ResultCsrdDto } from 'app/@types/dto/result-csrd-dto.model';
import { Observable, Subject, Subscription } from 'rxjs';

@Injectable({
    providedIn: 'root',
    deps: [QuestionnaireCsrdHttpService],
})
export class ResultsCsrdFetcherService {
    private valid: boolean = false;
    private lastValue: ResultCsrdDto[] | undefined;
    private orgId: string | undefined;
    private questionnaireId: string | undefined;
    private _subject$ = new Subject<ResultCsrdDto[]>();
    private _lastFetch: Subscription | undefined;

    constructor(private questionnaireService: QuestionnaireCsrdHttpService) {}

    private fetchIfPossible() {
        if (this.valid || this.orgId === undefined || this.questionnaireId === undefined) {
            return;
        }
        const subscription = this.questionnaireService.getResults(this.orgId, this.questionnaireId).subscribe({
            next: (results) => {
                /**
                 * Prevent double broadcast in the following use case:
                 * ```
                 * getCurrentResult().subscribe(...)
                 * setInvalid()
                 * setCurrentResult()
                 * ```
                 *
                 * With the fix in place, this will ONLY broadcast the result of the setCurrentResult() instead of broadcasting the result of setInvalid and
                 * setCurrentResult (without any specific order, as the first fetch isn't guaranteed to finish before the second fetch...).
                 */
                if (subscription === this._lastFetch) {
                    this._subject$.next(results);
                    this.lastValue = results;
                    this.valid = true;
                }
            },
            error: (err) => {
                if (subscription === this._lastFetch) {
                    this._subject$.error(err ?? 'fetch error');
                }
            },
        });
        this._lastFetch = subscription;
    }

    /**
     * Sets the cache in a dirty state. If the cache is currently being observed, **this will trigger a fetch**. Only the last triggered fetch will broadcast to the cache's reader.
     */
    public setInvalid(): void {
        this.valid = false;
        if (this._subject$.observed) {
            this.fetchIfPossible();
        }
    }

    /**
     * Sets the current result we want to fetch. If it is different from the previous result, the cache is set in a dirty state.
     * If the cache is currently being observed, **this will trigger a fetch**. Only the last triggered fetch will broadcast to the cache's reader.
     * @param orgId new organization id
     * @param questionnaireId new questionnaire id
     */
    public setCurrentResult(orgId: string, questionnaireId: string): void {
        if (this.orgId === orgId && this.questionnaireId === questionnaireId) {
            return;
        } // Noop
        this.orgId = orgId;
        this.questionnaireId = questionnaireId;
        this.valid = false;
        if (this._subject$.observed) {
            this.fetchIfPossible();
        }
    }

    /**
     *
     * @returns An observable that streams all the new results. If the cache was in a valid state, this will return the cached data. If it was dirty, it will trigger a fetch to update
     * the cache.
     */
    public getCurrentResult(): Observable<ResultCsrdDto[]> {
        if (
            this.valid &&
            this.orgId !== undefined &&
            this.questionnaireId !== undefined &&
            this.lastValue !== undefined
        ) {
            // Using a timeout so we return the subject immediately and the next will ALWAYS be invoked AFTER the return, as a subject can only be notified AFTER being subscribed
            setTimeout(() => {
                /* Also doing it inside timeout because by the time it executes, we aren't sure if it'd be still valid
                Take for example the following code (and assuming the current results are correctly cached, so valid is at true, orgid, questionnaireid and lastvalue are all defined)
                ```
                1. this.resultsFetcherService.getCurrentResult().subscribe(...)
                2. this.resultsFetcherService.setCurrentResult(...)
                ```

                First. The timeout only executes **after** the current code is done being ran. So in this minimal example, **after** line 2 is done being executed.
                This means that on line 1, it will **queue** the timeout. Then line 2 executes and sets the valid flag to false (and does other things) and queues a fetch.
                If the condition wasn't replicated inside the timeout, this would cause an unnecessary double next as it would first broadcast the stale value (as the fetch call 
                happened after the setTimeout) then it will broadcast the fresh value. Replicating the condition ensures that we are only broadcasting the cached value if there
                are no other fetch queued.
                */
                if (
                    this.valid &&
                    this.orgId !== undefined &&
                    this.questionnaireId !== undefined &&
                    this.lastValue !== undefined
                ) {
                    this._subject$.next(this.lastValue);
                }
            });
        } else {
            this.fetchIfPossible();
        }
        return this._subject$;
    }
}
